import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface TopCompaniesTableProps {
  companies: Array<{
    id: string;
    name: string;
    sessionCount: number;
    lastActivity: string;
    growth: number;
  }>;
  loading?: boolean;
  onCompanyClick?: (companyId: string) => void;
}

export function TopCompaniesTable({
  companies,
  loading = false,
  onCompanyClick,
}: TopCompaniesTableProps) {
  const getTrendIcon = (growth: number) => {
    if (growth > 0) return <ArrowUp className="h-3 w-3" />;
    if (growth < 0) return <ArrowDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = (growth: number) => {
    if (growth > 0) return "text-green-600 dark:text-green-400";
    if (growth < 0) return "text-red-600 dark:text-red-400";
    return "text-muted-foreground";
  };

  const formatLastActivity = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "Unknown";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Companies by Session Volume</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-12 w-full bg-muted animate-pulse rounded"
              />
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No company data available
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Growth</TableHead>
                  <TableHead className="text-right">Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow
                    key={company.id}
                    className={cn(
                      onCompanyClick && "cursor-pointer hover:bg-muted/50"
                    )}
                    onClick={() => onCompanyClick?.(company.id)}
                  >
                    <TableCell className="font-medium">
                      {company.name}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{company.sessionCount}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={cn(
                          "flex items-center justify-end gap-1 text-xs font-medium",
                          getTrendColor(company.growth)
                        )}
                      >
                        {getTrendIcon(company.growth)}
                        {Math.abs(company.growth).toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatLastActivity(company.lastActivity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
